import { z } from 'zod'
import { uuidSchema } from '../validators/uuid.validator.js'
import { problemSolutionDtoSchema } from './problem-solution.dto.js'

/**
 * A kind of problem this business meets, and what to do about it.
 *
 * Configured in the app rather than compiled in, for the same reason species
 * are: the list is a property of the business and the region it works, not of
 * the software. A crew moving south meets ball moss; a crew moving north meets
 * emerald ash borer, and neither is something to ship a release for.
 *
 * The three text fields answer three different questions and are kept apart
 * because they are read by different people at different moments:
 *
 * - `name` is what a surveyor taps in a list while standing under a tree.
 * - `description` is what the problem IS, written for a client — it is the
 *   sentence that turns a line item into an explanation.
 * - `solution` is what the work is, also client-facing, and the reason the
 *   quote says what it says.
 *
 * Both of the latter reach a proposal, which is why neither is a place for
 * shorthand. "MT - prune out" is a note; "Mistletoe is a parasitic plant that
 * draws water from the host" is a reason somebody agrees to pay.
 */
export const treeProblemTypeDtoSchema = z.object({
  id: uuidSchema,
  name: z.string(),
  description: z.string().nullable(),
  solution: z.string().nullable(),
  /**
   * The stock photograph for this kind of problem, shown when a surveyor did
   * not take one of the actual tree.
   *
   * An id rather than a URL, because storage is private: every read is a
   * short-lived pre-signed link fetched per document, so a DTO that carried a
   * URL would carry one that expires. The same reason `TreePhotos` asks for a
   * link per thumbnail instead of deriving one.
   */
  photoDocumentId: uuidSchema.nullable(),
  /**
   * A clip explaining what this problem looks like, for a surveyor deciding
   * whether the tree in front of them has it.
   *
   * Distinct from the photo in purpose, not just in format. The photo STANDS
   * IN for a picture nobody took of the actual tree; this describes the kind
   * of problem. An id rather than a playback URL, because Stream links are
   * short-lived signed tokens — the same reason a photo is an id.
   */
  videoId: uuidSchema.nullable(),
  /**
   * Where it sits in the picker. Hand-ordered, because the useful order is
   * "what this crew meets most", which nothing can derive.
   */
  sortOrder: z.number().int(),
  /**
   * Archived, never deleted. A sent proposal explained a price using this
   * problem's description and solution, and trees surveyed years ago still
   * point at it; a hard delete would orphan both. Archived types keep their
   * trees and drop out of the picker.
   */
  archivedAt: z.string().datetime().nullable(),
  /**
   * The treatments offered for this problem, archived ones included.
   *
   * Nested rather than fetched separately: the settings screen needs them on
   * every row, and a surveyor picking a problem needs its solutions in the
   * same breath. A second round trip per problem would be a second round trip
   * per problem.
   */
  solutions: z.array(problemSolutionDtoSchema),

  /**
   * How many live trees carry this problem, so the settings screen can say
   * what archiving one would affect without every caller running a count.
   */
  treeCount: z.number().int().nonnegative(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
})

export type TreeProblemTypeDto = z.infer<typeof treeProblemTypeDtoSchema>

export const treeProblemTypeListDtoSchema = z.object({
  problemTypes: z.array(treeProblemTypeDtoSchema),
})

export type TreeProblemTypeListDto = z.infer<
  typeof treeProblemTypeListDtoSchema
>
